import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { BrandLockup, BrandLogoImage } from "@/components/Brand";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("demo@playbookdata.com");
  const [password, setPassword] = useState("playbook123");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name);
      navigate("/");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Left — brand panel (dark surface → black logo) */}
      <div className="relative hidden overflow-hidden bg-[#0E1417] p-12 text-white lg:flex lg:flex-col lg:justify-between">
        <div className="pointer-events-none absolute -left-24 top-1/3 h-96 w-96 rounded-full bg-[#35606E]/30 blur-3xl" />
        <div className="pointer-events-none absolute -right-10 bottom-10 h-72 w-72 rounded-full bg-[#6E97A2]/10 blur-3xl" />
        <BrandLogoImage variant="black" className="relative h-28 w-auto self-start object-contain" />
        <div className="relative">
          <h1 className="font-display text-5xl font-extrabold leading-[1.06] tracking-tight">
            Track how your<br />sound <span className="text-[#6E97A2]">performs.</span>
          </h1>
          <p className="mt-6 max-w-md font-sans text-[#A9B7BC]">
            Every creator post using your song across Instagram Reels and TikTok — aggregated into one live performance report.
          </p>
          <div className="mt-10 flex gap-8">
            {[["900K+", "Views tracked"], ["5.35%", "Avg. engagement"], ["Live", "Auto refresh"]].map(([n, l]) => (
              <div key={l}>
                <p className="num-display text-3xl font-extrabold">{n}</p>
                <p className="text-xs text-[#6E97A2]">{l}</p>
              </div>
            ))}
          </div>
        </div>
        <p className="relative font-sans text-xs text-[#4b5c62]">Sound-performance analytics for music marketing teams.</p>
      </div>

      {/* Right — form (light surface → cream lockup) */}
      <div className="flex items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-sm"
        >
          <div className="mb-8 lg:hidden">
            <BrandLockup />
          </div>
          <h2 className="font-display text-4xl font-extrabold tracking-tight text-[#14252B]">{mode === "login" ? "Welcome back" : "Create account"}</h2>
          <p className="mt-1 font-sans text-sm text-[#6B7A80]">{mode === "login" ? "Sign in to your campaigns." : "Start tracking your sounds today."}</p>

          <form onSubmit={submit} className="mt-8 space-y-4 font-sans">
            {mode === "register" && (
              <div>
                <Label>Name</Label>
                <Input data-testid="input-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1" placeholder="Your name" />
              </div>
            )}
            <div>
              <Label>Email</Label>
              <Input data-testid="input-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" required />
            </div>
            <div>
              <Label>Password</Label>
              <Input data-testid="input-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1" required />
            </div>
            {error && <p data-testid="auth-error" className="text-sm text-red-600">{error}</p>}
            <Button data-testid="submit-auth" type="submit" disabled={loading} className="group w-full rounded-full bg-[#35606E] py-6 text-base hover:bg-[#294b57]">
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {mode === "login" ? "Sign in" : "Create account"}
              {!loading && <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />}
            </Button>
          </form>

          <p className="mt-6 text-center font-sans text-sm text-[#6B7A80]">
            {mode === "login" ? "New here?" : "Already have an account?"}{" "}
            <button
              data-testid="toggle-auth-mode"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
              className="font-semibold text-[#35606E] underline underline-offset-4"
            >
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
